import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import OrderProgressTimeline from './components/OrderProgressTimeline';
import OrderSummaryCard from './components/OrderSummaryCard';
import DeliveryTracking from './components/DeliveryTracking';
import OrderDetails from './components/OrderDetails';
import OrderActions from './components/OrderActions';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const OrderTracking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);
  const [user, setUser] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shipmentData, setShipmentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Mock user data
  useEffect(() => {
    const mockUser = localStorage.getItem('currentUser');
    if (mockUser) {
      setUser(JSON.parse(mockUser));
    }

    const mockCartCount = localStorage.getItem('cartCount');
    if (mockCartCount) {
      setCartCount(parseInt(mockCartCount, 10));
    }

    // Check if tracking number passed via navigation state
    if (location?.state?.trackingNumber) {
      setTrackingNumber(location?.state?.trackingNumber);
      handleTrackShipment(location?.state?.trackingNumber);
    }
  }, [location]);

  // Real-time update simulation
  useEffect(() => {
    if (shipmentData) {
      const interval = setInterval(() => {
        setLastUpdate(new Date());
        // Simulate real-time status updates
        setShipmentData(prev => ({
          ...prev,
          lastUpdated: new Date()?.toISOString()
        }));
      }, 30000); // Update every 30 seconds

      return () => clearInterval(interval);
    }
  }, [shipmentData]);

  const handleTrackShipment = (trackingNum) => {
    const numToTrack = trackingNum || trackingNumber;
    
    if (!numToTrack?.trim()) {
      setError('Please enter a tracking number');
      return;
    }

    setIsLoading(true);
    setError(null);

    // Simulate API call
    setTimeout(() => {
      // Mock shipment data with real-time tracking
      const mockShipment = {
        trackingNumber: numToTrack,
        status: 'in_transit',
        currentLocation: 'Distribution Center - Los Angeles, CA',
        origin: 'Shanghai, China',
        destination: 'New York, NY, USA',
        estimatedDelivery: '2026-02-05',
        shipmentType: 'Express International',
        weight: '25.5 kg',
        dimensions: '60x40x30 cm',
        carrier: 'Dream Work Express',
        service: 'Air Cargo - Priority',
        lastUpdated: new Date()?.toISOString(),
        timeline: [
          {
            status: 'booked',
            label: 'Shipment Booked',
            timestamp: '2026-01-25T10:30:00Z',
            location: 'Shanghai, China',
            description: 'Shipment booking confirmed and label created',
            completed: true
          },
          {
            status: 'picked_up',
            label: 'Picked Up',
            timestamp: '2026-01-26T08:15:00Z',
            location: 'Shanghai Warehouse',
            description: 'Package collected from sender',
            completed: true
          },
          {
            status: 'customs_cleared',
            label: 'Customs Cleared',
            timestamp: '2026-01-27T14:20:00Z',
            location: 'Shanghai Port',
            description: 'Export customs clearance completed',
            completed: true
          },
          {
            status: 'in_transit',
            label: 'In Transit',
            timestamp: '2026-01-28T06:45:00Z',
            location: 'Los Angeles Distribution Center',
            description: 'Package in transit to destination country',
            completed: true,
            current: true
          },
          {
            status: 'out_for_delivery',
            label: 'Out for Delivery',
            timestamp: null,
            location: 'New York Local Hub',
            description: 'Package will be out for delivery',
            completed: false
          },
          {
            status: 'delivered',
            label: 'Delivered',
            timestamp: null,
            location: 'New York, NY',
            description: 'Package delivered to recipient',
            completed: false
          }
        ],
        items: [
          {
            description: 'Electronics - Laptop Computer',
            quantity: 2,
            weight: '15 kg',
            value: '$2,500'
          },
          {
            description: 'Accessories - Cables & Adapters',
            quantity: 5,
            weight: '10.5 kg',
            value: '$350'
          }
        ],
        recipient: {
          name: 'John Smith',
          address: '123 Business Ave, Suite 400',
          city: 'New York',
          state: 'NY',
          zip: '10001',
          country: 'USA',
          phone: '+1 (555) 123-4567'
        },
        sender: {
          name: 'Tech Supplies Co.',
          address: '456 Manufacturing Rd',
          city: 'Shanghai',
          country: 'China',
          phone: '+86 21 1234 5678'
        }
      };

      setShipmentData(mockShipment);
      setIsLoading(false);
    }, 1500);
  };

  const handleCartClick = () => {
    navigate('/shopping-cart');
  };

  const handleAccountClick = (action) => {
    switch (action) {
      case 'login': navigate('/login');
        break;
      case 'register': navigate('/register');
        break;
      case 'account': navigate('/user-account');
        break;
      case 'logout': localStorage.removeItem('currentUser');
        setUser(null);
        break;
      default:
        break;
    }
  };

  const handleSearch = (searchTerm) => {
    navigate('/menu-catalog', { state: { searchQuery: searchTerm } });
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    setUser(null);
  };

  const handleContactSupport = () => {
    // In real app, this would open support chat or contact form
    alert('Support contact: support@dreamworkshipping.com or call +1-800-SHIP-NOW');
  };

  const handlePrintLabel = () => {
    window.print();
  };

  const handleShareTracking = () => {
    if (navigator.share && shipmentData) {
      navigator.share({
        title: 'Shipment Tracking',
        text: `Track shipment ${shipmentData?.trackingNumber}`,
        url: window.location?.href
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard?.writeText(window.location?.href);
      alert('Tracking link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        cartCount={cartCount}
        user={user}
        onCartClick={handleCartClick}
        onAccountClick={handleAccountClick}
        onSearch={handleSearch}
        onLogout={handleLogout}
      />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-primary to-blue-600 text-primary-foreground py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full mb-4">
                <Icon name="MapPin" size={20} />
                <span className="font-body font-medium">REAL-TIME TRACKING</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-4">
                Track Your Shipment
              </h1>
              <p className="text-xl text-primary-foreground/90 font-body max-w-2xl mx-auto">
                Get real-time updates on your international shipments with live tracking
              </p>
            </motion.div>
          </div>
        </section>

        {/* Tracking Input Section */}
        <section className="py-12 bg-card">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-background rounded-2xl shadow-warm-lg p-8"
            >
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6 text-center">
                Enter Tracking Number
              </h2>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e?.target?.value)}
                    onKeyPress={(e) => e?.key === 'Enter' && handleTrackShipment()}
                    placeholder="e.g., DWS123456789"
                    className="w-full"
                  />
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => handleTrackShipment()}
                  disabled={isLoading}
                  iconName={isLoading ? 'Loader' : 'Search'}
                  iconPosition="left"
                  className="sm:w-auto w-full"
                >
                  {isLoading ? 'Tracking...' : 'Track'}
                </Button>
              </div>
              {error && (
                <p className="text-destructive text-sm mt-2 flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} />
                  {error}
                </p>
              )}
              <p className="text-muted-foreground text-sm mt-4 text-center">
                Track multiple shipments by entering tracking numbers separated by commas
              </p>
            </motion.div>
          </div>
        </section>

        {/* Tracking Results */}
        {shipmentData && (
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Real-time Update Indicator */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-center gap-2 mb-6 text-success"
              >
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span className="text-sm font-body">
                  Live tracking • Last updated: {lastUpdate?.toLocaleTimeString()}
                </span>
              </motion.div>

              <div className="grid lg:grid-cols-3 gap-8">
                {/* Main Tracking Column */}
                <div className="lg:col-span-2 space-y-8">
                  <OrderProgressTimeline timeline={shipmentData?.timeline} />
                  <DeliveryTracking shipment={shipmentData} />
                  <OrderDetails shipment={shipmentData} />
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  <OrderSummaryCard shipment={shipmentData} />
                  <OrderActions
                    onContactSupport={handleContactSupport}
                    onPrintLabel={handlePrintLabel}
                    onShare={handleShareTracking}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Empty State */}
        {!shipmentData && !isLoading && (
          <section className="py-20">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
              >
                <div className="w-32 h-32 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Package" size={64} className="text-muted-foreground" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-foreground mb-4">
                  Start Tracking Your Shipment
                </h3>
                <p className="text-muted-foreground font-body mb-8">
                  Enter your tracking number above to see real-time updates on your international shipment
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/table-reservation')}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Book New Shipment
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/user-account')}
                    iconName="User"
                    iconPosition="left"
                  >
                    View My Shipments
                  </Button>
                </div>
              </motion.div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default OrderTracking;